import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { StrengthStars } from "./Revision";
import {
  LayoutGrid, Table as TableIcon, Search, ArrowUpDown, HeartPulse, BookOpen, History,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const STATUS_META = {
  memorized: { label: "Memorized", cls: "status-memorized" },
  learning: { label: "Learning", cls: "status-learning" },
  revising: { label: "Revising", cls: "status-revising" },
  not_started: { label: "Not started", cls: "status-revising" },
};

const COLUMNS = [
  { key: "ayah", label: "Ayah" },
  { key: "status", label: "Status" },
  { key: "revision_count", label: "Revisions" },
  { key: "last_revised", label: "Last Revised" },
  { key: "mastery", label: "Mastery" },
  { key: "ai_score", label: "AI Score" },
  { key: "teacher_grade", label: "Teacher" },
  { key: "updated_at", label: "Date" },
];

export default function Progress() {
  const { tx, t } = useLanguage();
  const [rows, setRows] = useState(null);
  const [view, setView] = useState("timeline");
  const [statusFilter, setStatusFilter] = useState("all");
  const [surahFilter, setSurahFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "ayah", dir: 1 });

  useEffect(() => {
    api.get("/progress/tracker").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);

  const surahOptions = useMemo(() => [...new Set((rows || []).map((r) => r.surah))], [rows]);

  const filtered = useMemo(() => {
    let out = rows || [];
    if (statusFilter !== "all") out = out.filter((r) => r.status === statusFilter);
    if (surahFilter !== "all") out = out.filter((r) => r.surah === parseInt(surahFilter));
    const q = query.trim().toLowerCase();
    if (q) out = out.filter((r) => `${r.surah_name} ${r.surah}:${r.ayah} ayah ${r.ayah} ${r.teacher_note || ""}`.toLowerCase().includes(q));
    const dir = sort.dir;
    return [...out].sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av > bv ? 1 : av < bv ? -1 : 0) * dir;
    });
  }, [rows, statusFilter, surahFilter, query, sort]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }));

  const timeline = useMemo(() => {
    const events = [];
    filtered.forEach((r) => {
      if (r.updated_at) {
        events.push({
          date: r.updated_at,
          label: r.status === "memorized" ? "Memorized" : r.status === "revising" ? "Revising" : "Started learning",
          detail: `${r.surah_name} · Ayah ${r.ayah}`,
          meta: r.ai_score != null ? `AI accuracy ${r.ai_score}%` : null,
          row: r,
        });
      }
      if (r.last_revised) {
        events.push({
          date: r.last_revised,
          label: "Revision done",
          detail: `${r.surah_name} · Ayah ${r.ayah}`,
          meta: r.strength != null ? `Mastery ${r.mastery}%` : null,
          row: r,
        });
      }
    });
    events.sort((a, b) => (a.date < b.date ? 1 : -1));
    const groups = [];
    events.forEach((e) => {
      const day = new Date(e.date).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
      const g = groups.find((x) => x.day === day);
      if (g) g.items.push(e); else groups.push({ day, items: [e] });
    });
    return groups;
  }, [filtered]);

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="progress-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">{t("progress.learningTracker")}</div>
          <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">{t("progress.myProgress")}</h1>
        </div>
        <div className="flex rounded-xl border border-sand-300 overflow-hidden" data-testid="view-toggle">
          <button onClick={() => setView("timeline")} className={`px-3.5 py-2 text-sm inline-flex items-center gap-1.5 ${view === "timeline" ? "bg-forest-600 text-sand-100" : "bg-white hover:bg-sand-100"}`} data-testid="view-timeline-btn">
            <History className="h-4 w-4" /> {t("progress.timeline")}
          </button>
          <button onClick={() => setView("card")} className={`px-3.5 py-2 text-sm inline-flex items-center gap-1.5 ${view === "card" ? "bg-forest-600 text-sand-100" : "bg-white hover:bg-sand-100"}`} data-testid="view-card-btn">
            <LayoutGrid className="h-4 w-4" /> {t("progress.cards")}
          </button>
          <button onClick={() => setView("table")} className={`px-3.5 py-2 text-sm inline-flex items-center gap-1.5 ${view === "table" ? "bg-forest-600 text-sand-100" : "bg-white hover:bg-sand-100"}`} data-testid="view-table-btn">
            <TableIcon className="h-4 w-4" /> {t("progress.table")}
          </button>
        </div>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3" data-testid="progress-filters">
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("progress.search")}
            className="rounded-xl border border-sand-300 bg-white pl-9 pr-3 py-2 text-sm w-52" data-testid="progress-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm" data-testid="progress-status-filter">
          <option value="all">{t("progress.allStatuses")}</option>
          <option value="memorized">{t("hifzPlan.memorized")}</option>
          <option value="learning">{t("progress.learning")}</option>
          <option value="revising">{t("progress.revising")}</option>
        </select>
        <select value={surahFilter} onChange={(e) => setSurahFilter(e.target.value)}
          className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm" data-testid="progress-surah-filter">
          <option value="all">{t("surahView.allSurahs")}</option>
          {surahOptions.map((n) => <option key={n} value={n}>{t("progress.surah", { n })}</option>)}
        </select>
        <span className="text-xs text-muted-foreground">{t("common.ayahCount", { count: filtered.length })}</span>
      </div>

      {!rows ? (
        <div className="hifz-card h-64 animate-pulse-soft bg-sand-200/50" />
      ) : rows.length === 0 ? (
        <div className="hifz-card p-10 text-center" data-testid="progress-empty">
          <BookOpen className="h-8 w-8 mx-auto text-gold-600" />
          <div className="mt-3 font-display font-semibold text-forest-700">{t("progress.noTrackedAyahsYet")}</div>
          <p className="mt-1 text-sm text-muted-foreground">{t("progress.startMemorizingAndYourProgress")}</p>
          <Link to="/app/hifz" className="btn-primary mt-5 inline-flex text-sm">{t("tafsirReader.startMemorizing")}</Link>
        </div>
      ) : view === "timeline" ? (
        <div className="space-y-6" data-testid="progress-timeline">
          {timeline.map((g) => (
            <section key={g.day}>
              <div className="text-xs uppercase tracking-[0.18em] text-gold-700 font-semibold">{g.day}</div>
              <div className="mt-3 hifz-card p-0 divide-y divide-sand-200">
                {g.items.map((e, i) => (
                  <Link key={i} to={`/app/memorize/${e.row.surah}/${e.row.ayah}`} className="flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-sand-100/60 transition" data-testid={`timeline-event-${e.row.surah}-${e.row.ayah}-${i}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${
                        e.label === "Memorized" ? "bg-forest-600" : e.label === "Revision done" ? "bg-gold-500" : "bg-terracotta-500"
                      }`} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-forest-700">{tx(e.label)} · <span className="text-foreground/80">{e.detail}</span></div>
                        {e.meta && <div className="text-xs text-muted-foreground">{e.meta}</div>}
                      </div>
                    </div>
                    {e.row.strength != null && <StrengthStars value={e.row.strength} />}
                  </Link>
                ))}
              </div>
            </section>
          ))}
          {timeline.length === 0 && <div className="hifz-card p-8 text-center text-sm text-muted-foreground">{t("progress.noActivityMatchesYourFilters")}</div>}
        </div>
      ) : view === "table" ? (
        <div className="hifz-card p-0 overflow-x-auto" data-testid="progress-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-sand-300 text-left">
                {COLUMNS.map((c) => (
                  <th key={c.key} className="px-4 py-3 font-semibold text-forest-700 whitespace-nowrap">
                    <button onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 hover:text-gold-700" data-testid={`sort-${c.key}`}>
                      {tx(c.label)} <ArrowUpDown className="h-3 w-3 opacity-50" />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={`${r.surah}-${r.ayah}`} className="border-b border-sand-200 last:border-0 hover:bg-sand-100/60" data-testid={`progress-row-${r.surah}-${r.ayah}`}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <Link to={`/app/memorize/${r.surah}/${r.ayah}`} className="font-medium text-forest-700 hover:underline">
                      {r.surah_name} {r.surah}:{r.ayah}
                    </Link>
                    {r.recovery && <HeartPulse className="inline h-3.5 w-3.5 ml-1.5 text-terracotta-500" title={t("progress.inRecovery")} />}
                  </td>
                  <td className="px-4 py-3"><span className={`status-pill text-[10px] ${STATUS_META[r.status]?.cls}`}>{tx(STATUS_META[r.status]?.label)}</span></td>
                  <td className="px-4 py-3">{r.revision_count}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{r.last_revised ? new Date(r.last_revised).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">{r.strength != null ? <StrengthStars value={r.strength} /> : "—"}</td>
                  <td className="px-4 py-3">{r.ai_score != null ? t("progress.text", { ai_score: r.ai_score }) : "—"}</td>
                  <td className="px-4 py-3 max-w-[180px]">
                    {r.teacher_grade != null ? (
                      <span title={r.teacher_note || ""}>{"★".repeat(r.teacher_grade)}{r.teacher_note ? ` · ${r.teacher_note.slice(0, 30)}${r.teacher_note.length > 30 ? "…" : ""}` : ""}</span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="progress-cards">
          {filtered.map((r) => (
            <Link key={`${r.surah}-${r.ayah}`} to={`/app/memorize/${r.surah}/${r.ayah}`} className="hifz-card hifz-card-hover" data-testid={`progress-card-${r.surah}-${r.ayah}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-display font-semibold text-forest-700">{r.surah_name} {r.surah}:{r.ayah}</div>
                <span className={`status-pill text-[10px] ${STATUS_META[r.status]?.cls}`}>{tx(STATUS_META[r.status]?.label)}</span>
              </div>
              <div className="mt-3 flex items-center gap-2">
                {r.strength != null ? <StrengthStars value={r.strength} /> : <span className="text-xs text-muted-foreground">{t("progress.noReviewsYet")}</span>}
                {r.recovery && <span className="text-[10px] font-semibold text-terracotta-600">{t("progress.recovery")}</span>}
              </div>
              <div className="mt-3 text-xs text-muted-foreground space-y-1">
                <div>{t("progress.revisionsLast", { count: r.revision_count, when: r.last_revised ? t("progress.lastDate", { date: new Date(r.last_revised).toLocaleDateString() }) : t("progress.neverRevised") })}</div>
                <div>{t("progress.aiTeacher", { ai: r.ai_score != null ? `${r.ai_score}%` : "—", teacher: r.teacher_grade != null ? "★".repeat(r.teacher_grade) : "—" })}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
